import React, { useState, useEffect } from 'react';
import { actions as actionsApi } from '../../api/client';
import { Activity, Plus, Play, Loader2, MessageSquare, FileText, Zap, CheckCircle2, XCircle, Clock } from 'lucide-react';

const Badge = ({ children, variant = "default" }) => {
  const variants = {
    default: "bg-stone-100 text-stone-600 dark:bg-stone-800 dark:text-stone-300",
    success: "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400",
    warning: "bg-amber-50 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400",
    danger: "bg-rose-50 text-rose-700 dark:bg-rose-900/50 dark:text-rose-400",
    info: "bg-sky-50 text-sky-700 dark:bg-sky-900/50 dark:text-sky-400",
  };
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium ${variants[variant]}`}>{children}</span>;
};

const statusVariant = (s) => ({ COMPLETED: 'success', IN_PROGRESS: 'warning', FAILED: 'danger', PENDING: 'default', QUEUED: 'info' }[s] || 'default');
const typeIcon = (t) => {
  if (t?.includes('REDDIT')) return MessageSquare;
  if (t?.includes('CONTENT') || t?.includes('CAPSULE')) return FileText;
  return Zap;
};

export default function ActionsPage({ activeBrandId }) {
  const [actionsList, setActionsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [newAction, setNewAction] = useState({ type: 'CUSTOM', title: '', description: '', impact: 'MEDIUM' });
  const [creating, setCreating] = useState(false);

  const loadActions = async () => {
    if (!activeBrandId) return;
    setLoading(true);
    try {
      const params = filter ? { status: filter } : {};
      const data = await actionsApi.list(activeBrandId, params);
      setActionsList(data.actions || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadActions(); }, [activeBrandId, filter]);

  const handleCreate = async () => {
    if (!newAction.title.trim()) return;
    setCreating(true);
    try {
      await actionsApi.create(activeBrandId, newAction);
      setShowCreate(false);
      setNewAction({ type: 'CUSTOM', title: '', description: '', impact: 'MEDIUM' });
      loadActions();
    } catch (e) { console.error(e); }
    finally { setCreating(false); }
  };

  const handleRun = async (actionId) => {
    try {
      await actionsApi.update(activeBrandId, actionId, { runNow: true });
      loadActions();
    } catch (e) { console.error(e); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <span className="text-xs font-medium tracking-widest uppercase block mb-2 text-stone-500">Automation</span>
          <h1 className="font-serif text-2xl md:text-3xl text-stone-900 dark:text-white">Actions</h1>
        </div>
        <div className="flex items-center gap-2">
          <select value={filter} onChange={(e) => setFilter(e.target.value)}
            className="px-3 py-1.5 text-sm border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-700 dark:text-stone-300">
            <option value="">All statuses</option>
            <option value="PENDING">Pending</option>
            <option value="IN_PROGRESS">In Progress</option>
            <option value="COMPLETED">Completed</option>
            <option value="FAILED">Failed</option>
          </select>
          <button onClick={() => setShowCreate(true)} className="px-4 py-1.5 text-sm bg-stone-900 text-white dark:bg-white dark:text-stone-900 flex items-center gap-1">
            <Plus className="w-4 h-4" /> New Action
          </button>
        </div>
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowCreate(false)} />
          <div className="relative bg-white dark:bg-stone-900 w-full max-w-lg border border-stone-200 dark:border-stone-700 shadow-xl p-6">
            <h2 className="font-serif text-xl text-stone-900 dark:text-white mb-4">Create Action</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1">Type</label>
                <select value={newAction.type} onChange={(e) => setNewAction({...newAction, type: e.target.value})}
                  className="w-full px-3 py-2 border border-stone-300 dark:border-stone-600 bg-white dark:bg-stone-800 text-stone-900 dark:text-white">
                  <option value="REDDIT_POST">Reddit Post</option>
                  <option value="REDDIT_COMMENT">Reddit Comment</option>
                  <option value="CONTENT_CAPSULE">Content Capsule</option>
                  <option value="SCHEMA_DEPLOY">Schema Deploy</option>
                  <option value="LISTICLE_OUTREACH">Listicle Outreach</option>
                  <option value="CUSTOM">Custom</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1">Title</label>
                <input value={newAction.title} onChange={(e) => setNewAction({...newAction, title: e.target.value})}
                  className="w-full px-3 py-2 border border-stone-300 dark:border-stone-600 bg-white dark:bg-stone-800 text-stone-900 dark:text-white" placeholder="Action title" />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1">Impact</label>
                <select value={newAction.impact} onChange={(e) => setNewAction({...newAction, impact: e.target.value})}
                  className="w-full px-3 py-2 border border-stone-300 dark:border-stone-600 bg-white dark:bg-stone-800 text-stone-900 dark:text-white">
                  <option value="HIGH">High</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="LOW">Low</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm border border-stone-200 text-stone-600">Cancel</button>
              <button onClick={handleCreate} disabled={creating} className="px-4 py-2 text-sm bg-stone-900 text-white flex items-center gap-1">
                {creating && <Loader2 className="w-4 h-4 animate-spin" />} Create
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-stone-400" /></div>
      ) : (
        <div className="space-y-3">
          {actionsList.length === 0 ? (
            <div className="bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 p-8 text-center text-stone-500">No actions found.</div>
          ) : (
            actionsList.map((action) => {
              const Icon = typeIcon(action.type);
              return (
                <div key={action.action_id} className="bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 p-4 hover:border-stone-400 dark:hover:border-stone-500 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded flex items-center justify-center flex-shrink-0 bg-stone-100 dark:bg-stone-700">
                        <Icon className="w-5 h-5 text-stone-600 dark:text-stone-300" />
                      </div>
                      <div>
                        <p className="font-medium text-stone-900 dark:text-white">{action.title}</p>
                        <p className="text-sm text-stone-500 mt-0.5">{action.description}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant={statusVariant(action.status)}>
                            {action.status === 'IN_PROGRESS' && <Loader2 className="w-3 h-3 animate-spin" />}
                            {action.status}
                          </Badge>
                          <Badge>Impact: {action.impact}</Badge>
                          <span className="text-xs text-stone-400">{action.type?.replace(/_/g, ' ')}</span>
                        </div>
                      </div>
                    </div>
                    {(action.status === 'PENDING' || action.status === 'QUEUED') && (
                      <button onClick={() => handleRun(action.action_id)}
                        className="px-3 py-1.5 text-sm bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-1 flex-shrink-0">
                        <Play className="w-4 h-4" /> Run
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
